import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ADMIN_ROLE_ID, HOUSEKEEPING_ROLE_ID, FRONT_DESK_ROLE_ID } from '@/lib/roles';

function FullScreenLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50">
      <div className="text-center">
        <span className="w-10 h-10 flex items-center justify-center mx-auto mb-4">
          <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin"></i>
        </span>
        <p className="text-sm text-foreground-600">確認中...</p>
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, profile, loading } = useAuth();

  if (loading) return <FullScreenLoading />;
  if (!session || !profile) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { profile, loading } = useAuth();

  if (loading) return <FullScreenLoading />;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.access_level !== 'super_admin') return <Navigate to="/cleaning" replace />;
  return <>{children}</>;
}

export function RequireOperations({ children }: { children: ReactNode }) {
  const { profile, loading } = useAuth();
  if (loading) return <FullScreenLoading />;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.access_level !== 'super_admin' && !profile.permissions.includes('manage_operations')) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function RequireStaff({ children }: { children: ReactNode }) {
  const { profile, loading } = useAuth();

  if (loading) return <FullScreenLoading />;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.role_id !== ADMIN_ROLE_ID && profile.role_id !== HOUSEKEEPING_ROLE_ID && profile.role_id !== FRONT_DESK_ROLE_ID) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

export function RequireFrontDeskOrAdmin({ children }: { children: ReactNode }) {
  const { profile, loading } = useAuth();

  if (loading) return <FullScreenLoading />;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.role_id !== ADMIN_ROLE_ID && profile.role_id !== FRONT_DESK_ROLE_ID) {
    return <Navigate to="/cleaning" replace />;
  }
  return <>{children}</>;
}

export function RequireHousekeepingOrAdmin({ children }: { children: ReactNode }) {
  const { profile, loading } = useAuth();

  if (loading) return <FullScreenLoading />;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.role_id !== ADMIN_ROLE_ID && profile.role_id !== HOUSEKEEPING_ROLE_ID) {
    return <Navigate to="/reservations" replace />;
  }
  return <>{children}</>;
}
